import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { VerificationSettings } from '../types';

interface VerificationSettingsCardProps {
  settings: VerificationSettings;
  onUpdate: (settings: VerificationSettings) => void;
}

const VerificationSettingsCard = ({ settings, onUpdate }: VerificationSettingsCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(settings);

  const handleSave = () => {
    onUpdate(formData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData(settings);
    setIsEditing(false);
  };

  return (
    <div className="bg-card rounded-md border border-border p-6 shadow-elevation-1">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-success/10 rounded-md flex items-center justify-center">
            <Icon name="ShieldCheck" size={24} color="var(--color-success)" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Verification Settings</h2>
            <p className="text-sm text-muted-foreground">
              Configure contribution approval workflow
            </p>
          </div>
        </div>
        {!isEditing && (
          <Button
            variant="outline"
            iconName="Edit"
            iconPosition="left"
            onClick={() => setIsEditing(true)}
          >
            Edit
          </Button>
        )}
      </div>

      <div className="space-y-4">
        {isEditing ? (
          <>
            <Checkbox
              label="Require Admin Verification"
              description="All contributions must be approved by an admin before being recorded"
              checked={formData.requireAdminVerification}
              onChange={(e) =>
                setFormData({ ...formData, requireAdminVerification: e.target.checked })
              }
            />
            <Checkbox
              label="Require Second User Confirmation"
              description="New contributions need confirmation from another user"
              checked={formData.requireSecondUserConfirmation}
              onChange={(e) =>
                setFormData({ ...formData, requireSecondUserConfirmation: e.target.checked })
              }
            />
            <Checkbox
              label="Allow Member Edits"
              description="Members can edit their own contribution entries"
              checked={formData.allowMemberEdits}
              onChange={(e) =>
                setFormData({ ...formData, allowMemberEdits: e.target.checked })
              }
            />
            <Checkbox
              label="Auto-Approve Contributions"
              description="Automatically approve contributions without manual review"
              checked={formData.autoApproveContributions}
              onChange={(e) =>
                setFormData({ ...formData, autoApproveContributions: e.target.checked })
              }
            />
            <div className="flex gap-3 pt-4">
              <Button variant="default" onClick={handleSave}>
                Save Settings
              </Button>
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-muted rounded-sm">
              <div className="flex items-center gap-3">
                <Icon
                  name={settings.requireAdminVerification ? 'CheckCircle' : 'Circle'}
                  size={20}
                  color={
                    settings.requireAdminVerification
                      ? 'var(--color-success)'
                      : 'var(--color-muted-foreground)'
                  }
                />
                <span className="text-sm font-medium text-foreground">
                  Admin Verification
                </span>
              </div>
              <span className="text-xs text-muted-foreground">
                {settings.requireAdminVerification ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-muted rounded-sm">
              <div className="flex items-center gap-3">
                <Icon
                  name={settings.requireSecondUserConfirmation ? 'CheckCircle' : 'Circle'}
                  size={20}
                  color={
                    settings.requireSecondUserConfirmation
                      ? 'var(--color-success)'
                      : 'var(--color-muted-foreground)'
                  }
                />
                <span className="text-sm font-medium text-foreground">
                  Second User Confirmation
                </span>
              </div>
              <span className="text-xs text-muted-foreground">
                {settings.requireSecondUserConfirmation ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-muted rounded-sm">
              <div className="flex items-center gap-3">
                <Icon
                  name={settings.allowMemberEdits ? 'CheckCircle' : 'Circle'}
                  size={20}
                  color={
                    settings.allowMemberEdits
                      ? 'var(--color-success)'
                      : 'var(--color-muted-foreground)'
                  }
                />
                <span className="text-sm font-medium text-foreground">Member Edits</span>
              </div>
              <span className="text-xs text-muted-foreground">
                {settings.allowMemberEdits ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-muted rounded-sm">
              <div className="flex items-center gap-3">
                <Icon
                  name={settings.autoApproveContributions ? 'CheckCircle' : 'Circle'}
                  size={20}
                  color={
                    settings.autoApproveContributions
                      ? 'var(--color-success)'
                      : 'var(--color-muted-foreground)'
                  }
                />
                <span className="text-sm font-medium text-foreground">Auto-Approve</span>
              </div>
              <span className="text-xs text-muted-foreground">
                {settings.autoApproveContributions ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerificationSettingsCard;